/**
 * Variant Card Spreader (Featured Only)
 * Version 4.0 - Invisible & Instant
 * 
 * ROOT CAUSE OF "DOUBLE FLASH":
 * The curtain system itself was the problem. When globoFilterRenderCompleted fires,
 * multiple scripts respond (sale filter at 50ms, wishlist at 2000ms, etc). 
 * The curtain going up/down created a visible gray flash. When another script then 
 * modified the DOM, it appeared to flash a second time.
 *
 * SOLUTION:
 * Remove the curtain entirely. DocumentFragment-based DOM reorder takes <5ms,
 * which is invisible between animation frames. We wait 500ms after Globo's event
 * to ensure the sale filter (50ms) has finished first, then do a single instant reorder.
 */
(function () {
    const DEBUG = false;
    const WAIT_FOR_OTHER_SCRIPTS = 500;  // wait for collection.js sale filter (50ms) + buffer
    const COOLDOWN_MS = 2000;            // ignore triggers for 2s after a successful sort

    function log(msg, ...args) {
        if (DEBUG) console.log(`%c[Spreader] ${msg}`, 'color: #ff00ff; font-weight: bold;', ...args);
    }

    // --- UTILS ---

    function getSortParam() {
        return new URLSearchParams(window.location.search).get('sort_by');
    }

    function isTargetSort() {
        const s = getSortParam();
        return s === null || s === '' || s === 'featured' || s === 'bestmatch';
    }

    function isCollectionPage() {
        const p = window.location.pathname;
        return p.includes('/collections/') && !p.includes('/products/');
    }

    function getGrid() {
        return document.querySelector('#gf-products, #product-grid, ul#product-grid');
    }

    // Build a fingerprint of product IDs in current order
    function fingerprint(grid) {
        if (!grid) return '';
        const els = grid.querySelectorAll(':scope > li.grid__item [data-product-id]');
        let fp = '';
        els.forEach(el => { fp += el.dataset.productId + ','; });
        return fp;
    }

    // --- WAVE SORT ---

    function waveSort(cards) {
        const counts = {};
        const data = cards.map((card, i) => {
            const w = card.querySelector('[data-product-id]');
            const id = w ? w.dataset.productId : null;
            let wave = 0;
            if (id) {
                counts[id] = counts[id] || 0;
                wave = counts[id];
                counts[id]++;
            }
            return { card, i, wave };
        });

        data.sort((a, b) => a.wave !== b.wave ? a.wave - b.wave : a.i - b.i);
        return data.map(d => d.card);
    }

    // --- SINGLE PROCESS CONTROLLER ---

    let timer = null;
    let lastFingerprint = '';
    let lastSortTime = 0;

    function schedule(reason) {
        if (!isCollectionPage() || !isTargetSort()) {
            log(`Skip schedule: not applicable (${reason})`);
            return;
        }

        // Cooldown: ignore if we just sorted
        if (reason !== 'Load' && (Date.now() - lastSortTime) < COOLDOWN_MS) {
            log(`Cooldown active, ignoring: ${reason}`);
            return;
        }

        log(`Scheduled: ${reason}`);

        // Cancel any pending sort — only the LAST call wins
        clearTimeout(timer);

        // Wait for other scripts (sale filter etc.) to finish their DOM work first
        const delay = reason === 'Load' ? 100 : WAIT_FOR_OTHER_SCRIPTS;
        timer = setTimeout(() => doSort(reason), delay);
    }

    function doSort(reason) {
        const grid = getGrid();
        if (!grid) return;

        const cards = Array.from(grid.querySelectorAll(':scope > li.grid__item'));
        if (cards.length < 3) return;

        // Check if content actually needs sorting
        const fp = fingerprint(grid);
        if (fp === lastFingerprint && reason !== 'Load') {
            log('Skip: identical fingerprint');
            return;
        }

        // Perform the sort
        const sorted = waveSort(cards);

        // Check: did the sort actually change anything?
        let changed = false;
        for (let i = 0; i < sorted.length; i++) {
            if (sorted[i] !== cards[i]) {
                changed = true;
                break;
            }
        }

        if (!changed) {
            log('Skip: cards already in correct order');
            lastFingerprint = fp;
            lastSortTime = Date.now();
            return;
        }

        // Apply sort — this is instant (<5ms), no curtain needed
        const frag = document.createDocumentFragment();
        sorted.forEach(card => frag.appendChild(card));
        grid.appendChild(frag);

        lastFingerprint = fingerprint(grid);
        lastSortTime = Date.now();
        log(`Sorted ${cards.length} cards (${reason})`);
    }

    // --- INIT ---

    function init() {
        if (!isCollectionPage()) return;

        // Initial sort
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => schedule('Load'), { once: true });
        } else {
            schedule('Load');
        }

        // After Globo finishes rendering new filter results
        window.addEventListener('globoFilterRenderCompleted', () => {
            schedule('GloboRender');
        });

        // Sort dropdown
        document.addEventListener('change', (e) => {
            if (e.target.closest('.sort-by') || e.target.id === 'sort-by-dropdown' || e.target.id === 'sort-by-dropdown-mobile') {
                lastFingerprint = '';
                schedule('SortChange');
            }
        });

        // Back/forward
        window.addEventListener('popstate', () => {
            lastFingerprint = '';
            schedule('PopState');
        });
    }

    init();
})();
